import { Injectable } from '@angular/core';
import { PageTranslations } from '../interfaces/page-translations';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, catchError, forkJoin, map } from 'rxjs';
import { Translations } from '../interfaces/translations';
import { Router } from '@angular/router';

@Injectable({
     providedIn: 'root'
})
export class TranslatorService {
     langsPerPath: PageTranslations[] = [];
     translations: Translations[] = [];
     filesLoaded: boolean = false;
     activeLanguage: string = "en";
     currentRoute: string = "";
     hasChanges = new BehaviorSubject<boolean>(false);
     _hasChanges$ = this.hasChanges.asObservable();

     constructor(private http: HttpClient, private router: Router) {
          this.tempTranslate();
          router.events.subscribe(event => {
               this.currentRoute = router.url;
               const regex = /\/[\d\w]/;
               if (this.currentRoute.includes("@") || regex.test(this.currentRoute)) {
                    const lastDexOfSlash = this.currentRoute.lastIndexOf('/');
                    if (lastDexOfSlash > 0) {
                         this.currentRoute = this.currentRoute.substring(0, lastDexOfSlash);
                    }
               }
               this.currentRoute = this.currentRoute.replace("/#", "");
          });

          const pathConfigs = this.router.config;
          pathConfigs.forEach((config: any) => {
               if (config.data && config.data.languages) {
                    this.langsPerPath.push({ path: '/' + config.path as any, languages: config.data.languages });
               }
          });

          const forceLoadValue = new Date().getTime();
          const url = `${environment.APP_API_URL}/api/v1/Translate/getJSON?v=` + forceLoadValue;


          this.http.get(url)
               .pipe(
                    map((response) => {
                         if (response) {
                              this.translations = response as any;
                              this.filesLoaded = true;
                         }
                         return null;
                    }),
                    catchError(error => {
                         console.error('Error fetching data:', error);
                         return [];
                    })
               )
               .subscribe();


     }

     public get translationsReady() {
          return this.filesLoaded;
     }

     public setActiveLanguage(value: string) {
          this.activeLanguage = value.toLocaleLowerCase();
          window.document.documentElement.lang = value.toLocaleLowerCase();
          this.hasChanges.next(true);
     }

     public resetHasChange() {
          this.hasChanges.next(false);
     }

     public getActiveLanguage() {
          return this.activeLanguage;
     }

     public backupLanguage() {
          const date = new Date();
          date.setTime(date.getTime() + (1 * 1 * 15 * 60 * 1000)); // 15 min
          document.cookie = `languageBeforeRedirect=${this.activeLanguage}; expires=${date.toUTCString()}`;
     }

     public restoreBackupLanguage() {
          var lang = this.getCookie('languageBeforeRedirect');
          if (lang) {
               this.setActiveLanguage(lang);
          }
     }

     public observeActiveLanguage: Observable<string> = new Observable<string>(observer => {
          return observer.next(this.activeLanguage);
     });

     public get getLanguagesRouteCanSee(): string[] {
          // if the current route is not in the list of routes that have languages, then it's the wild card route. Only return EN.
          let baseRoute = this.currentRoute;
          if (this.currentRoute.indexOf('?') > 0) {
               baseRoute = this.currentRoute.substring(0, this.currentRoute.indexOf('?'));
          }

          if (!this.langsPerPath.some(v => v.path === baseRoute)) {
               return ['EN'];
          }
          const pathLanguages = this.langsPerPath.find(v => v.path === baseRoute) || null;
          if (pathLanguages !== null) {
               return pathLanguages.languages;
          };
          return ['EN', 'ES', 'FR'];
     }

     public getTextForTag(tag: string) {
          let baseRoute = this.currentRoute;
          if (this.currentRoute.indexOf('?') > 0) {
               baseRoute = this.currentRoute.substring(0, this.currentRoute.indexOf('?'));
          }

          const _row = this.translations.find(t => t.tag === tag) || null;

          const pathLanguages = this.langsPerPath.find(v => v.path === baseRoute) || null;
          if (_row !== null) {
               if (!(pathLanguages?.languages.includes(this.activeLanguage.toUpperCase())) || this.activeLanguage === "en") {
                    return _row.en;
               } else if (this.activeLanguage == "es") {
                    return _row.es;
               } else if (this.activeLanguage == "fr") {
                    return _row.fr;
               } else {
                    return _row.en;
               }
          }
          return '';
     }

     getCookie(name: string): string | undefined {
          const value = `; ${document.cookie}`;
          const parts = value.split(`; ${name}=`);
          if (parts && parts.length === 2) {
               const val = parts.pop();
               if (val) {
                    return val.split(';').shift();
               } else {
                    console.error('Cookie null for ' + name);
                    return;
               }
          }
          return '';
     }

     tempTranslate() {
          //turn this data into and array of Translations and load it into this.translations


          // Convert the hardcoded translation data into an array of Translations and load it into this.translations
          this.translations = [
               { tag: "Enroll today!", en: "Enroll Today!", es: "¡Inscríbase hoy!", fr: "Inscrivez-vous aujourd'hui!" },
               { tag: "Online_PSP_card_2", en: "Click here", es: "Haga clic aquí", fr: "Cliquez ici" },
               { tag: "JavascriptAlert", en: "Javascript must be enabled to use all of the functionality of this site.", es: "JavaScript debe estar habilitado para usar todas las funciones de este sitio.", fr: "JavaScript doit être activé pour utiliser toutes les fonctionnalités de ce site." },
               { tag: "Contact", en: "Contact", es: "Contacto", fr: "Contact" },
               { tag: "Drivers", en: "Driver(s)", es: "Conductor(es)", fr: "Conducteur(s)" },
               { tag: "Enroll", en: "Enroll", es: "Inscribirse", fr: "S'inscrire" },
               { tag: "fmcsa-search", en: "Search", es: "Buscar", fr: "Recherche" },
               { tag: "Loading", en: "Loading...", es: "Cargando...", fr: "Chargement..." },
               { tag: "OK", en: "Ok", es: "Aceptar", fr: "D'accord" },
               { tag: "RequestYourRec", en: "Request Your Record", es: "Solicite su registro", fr: "Demandez votre dossier" },
               { tag: "sessionAlertSeconds", en: "seconds", es: "segundos", fr: "secondes" },
               { tag: "ToggleNav1", en: "Toggle ", es: "Alternar ", fr: "Basculer " },
               { tag: "Veterans", en: "Veterans", es: "Veteranos", fr: "Vétérans" },
               { tag: "WomenNav", en: "Women Drivers", es: "Mujeres conductoras", fr: "Femmes conductrices" },
               { tag: "FAQs", en: "FAQs", es: "Preguntas frecuentes", fr: "FAQ" },
               { tag: "HomeVeteransLink", en: "Learn More", es: "Aprende más", fr: "En savoir plus" },
               { tag: "landing1", en: "PSP has the data that you need to hire safe drivers.", es: "PSP tiene los datos que necesita para contratar conductores seguros.", fr: "Le PSP dispose des données dont vous avez besoin pour embaucher des conducteurs sûrs." },
               { tag: "navbar_login_link", en: "Login", es: "Iniciar sesión", fr: "Connexion" },
               { tag: "PreviousReq_To_View", en: "to view past records.", es: "para ver registros anteriores.", fr: "pour voir les dossiers précédents." },
               { tag: "sessionAlertExpire", en: "Your session will expire in", es: "Su sesión expirará en", fr: "Votre session expirera dans" },
               { tag: "TestEnvModalBody", en: "This is a test web site. To use the real website please go to ", es: "Este es un sitio web de prueba. Para usar el sitio real, vaya a ", fr: "Ceci est un site de test. Pour utiliser le vrai site, allez sur " },
               { tag: "ToggleNav2", en: "Navigation", es: "Navegación", fr: "Navigation" },
               { tag: "vet_card_to_learn_more", en: "to learn more!", es: "para saber más!", fr: "pour en savoir plus!" },
               { tag: "Warning", en: "Warning!", es: "¡Advertencia!", fr: "Avertissement!" },
               { tag: "welcomeBack", en: "Welcome Back", es: "Bienvenido de nuevo", fr: "Bon retour" },
               { tag: "home_driver_card", en: "You can use PSP to request and review your personal driving record at any time. The PSP report pulls your 5-year crash and 3-year roadside inspection history. You can also sign up to receive a notification through PSP Monitoring every time your PSP record is updated. This helps you keep track of changes to your PSP record. Previously requested records can be viewed for 5 days or 120 hours.", es: "Puede usar PSP para solicitar y revisar su registro de manejo personal en cualquier momento. El informe PSP muestra su historial de accidentes de 5 años y de inspecciones en carretera de 3 años. También puede inscribirse para recibir una notificación a través de PSP Monitoring cada vez que se actualice su registro PSP. Esto le ayuda a realizar un seguimiento de los cambios en su registro PSP. Los registros solicitados previamente pueden verse durante 5 días o 120 horas.", fr: "Vous pouvez utiliser le PSP pour demander et consulter votre dossier de conduite personnel à tout moment. Le rapport PSP fournit votre historique d'accidents sur 5 ans et d'inspections routières sur 3 ans. Vous pouvez également vous inscrire pour recevoir une notification via PSP Monitoring chaque fois que votre dossier PSP est mis à jour. Cela vous aide à suivre les changements dans votre dossier PSP. Les dossiers demandés précédemment peuvent être consultés pendant 5 jours ou 120 heures." },
               { tag: "home_vet_card", en: "Over 10% of commercial drivers in the U.S. have transitioned from military service to truck and bus drivers. As you continue your desire to support America, learn how your driving record data is captured and shared as you enter the commercial transportation industry. If you are interested in a career as a truck or bus driver, you'll want to know about the Pre-Employment Screening Program (PSP).", es: "Más del 10% de los conductores comerciales en EE. UU. han pasado del servicio militar a ser conductores de camiones y autobuses. Mientras continúa su deseo de apoyar a Estados Unidos, aprenda cómo se recopilan y comparten los datos de su registro de manejo al ingresar a la industria del transporte comercial. Si está interesado en una carrera como conductor de camión o autobús, querrá saber sobre el Programa de Selección Previa al Empleo (PSP).", fr: "Plus de 10 % des conducteurs professionnels aux États-Unis sont passés du service militaire à la conduite de camions et d'autobus. Alors que vous continuez à soutenir l'Amérique, découvrez comment les données de votre dossier de conduite sont collectées et partagées lorsque vous entrez dans l'industrie du transport commercial. Si vous souhaitez faire carrière comme conducteur de camion ou d'autobus, vous voudrez en savoir plus sur le Programme de présélection à l'emploi (PSP)." },
               { tag: "HomeVeteranHeader", en: "Are you a military veteran?", es: "¿Es usted un veterano militar?", fr: "Êtes-vous un vétéran militaire ?" },
               { tag: "landing2", en: "Companies using PSP to screen new hires <strong>lower their crash rate by 8%</strong> and <strong>driver out-of-service rates by 17%</strong>, on average, compared to those that do not use PSP.", es: "Las empresas que utilizan PSP para evaluar nuevas contrataciones <strong>reducen su tasa de accidentes en un 8%</strong> y <strong>las tasas de conductores fuera de servicio en un 17%</strong>, en promedio, en comparación con las que no usan PSP.", fr: "Les entreprises utilisant le PSP pour sélectionner de nouveaux employés <strong>réduisent leur taux d'accidents de 8 %</strong> et <strong>le taux de conducteurs hors service de 17 %</strong>, en moyenne, par rapport à celles qui n'utilisent pas le PSP." },
               { tag: "NeedHelp", en: "Forgot your password?", es: "¿Olvidó su contraseña?", fr: "Mot de passe oublié ?" },
               { tag: "PasswordVideo", en: "Need help creating your Login.gov account?", es: "¿Necesita ayuda para crear su cuenta Login.gov?", fr: "Besoin d'aide pour créer votre compte Login.gov ?" },
               { tag: "PreviousReq_link", en: "Log into your account", es: "Inicie sesión en su cuenta", fr: "Connectez-vous à votre compte" },
               { tag: "sessionAlertBody", en: "Your session is about to expire due to inactivity. Please click <em>I'm still here</em> to continue.", es: "Su sesión está a punto de expirar por inactividad. Haga clic en <em>Sigo aquí</em> para continuar.", fr: "Votre session est sur le point d'expirer en raison de l'inactivité. Veuillez cliquer sur <em>Je suis toujours là</em> pour continuer." },
               { tag: "sessionAlertButton", en: "I'm still here", es: "Sigo aquí", fr: "Je suis toujours là" },
               { tag: "sessionAlertHeader", en: "Are you still there?", es: "¿Sigues ahí?", fr: "Êtes-vous toujours là ?" },
               { tag: "welcome_box_1", en: "Multi-factor authentication is now required to access PSP.", es: "Ahora se requiere autenticación multifactor para acceder a PSP.", fr: "L'authentification multifacteur est désormais requise pour accéder au PSP." },
               { tag: "welcome_box_2", en: "Click on the LOGIN button below. You will be redirected to Login.gov. ", es: "Haga clic en el botón INICIAR SESIÓN a continuación. Será redirigido a Login.gov. ", fr: "Cliquez sur le bouton CONNEXION ci-dessous. Vous serez redirigé vers Login.gov. " },
               { tag: "welcome_box_3", en: "From here, you can create an account, log in, or reset your password.", es: "Desde aquí, puede crear una cuenta, iniciar sesión o restablecer su contraseña.", fr: "À partir d'ici, vous pouvez créer un compte, vous connecter ou réinitialiser votre mot de passe." },
               { tag: "aboutPSP", en: "About PSP", es: "Sobre PSP", fr: "À propos de PSP" },
               { tag: "driver_subscribe", en: "Subscribe", es: "Suscribirse", fr: "S'abonner" },
               { tag: "WhatIsMonitoring", en: "What is PSP monitoring?", es: "¿Qué es la monitorización PSP?", fr: "Qu'est-ce que la surveillance PSP ?" },
               { tag: "ContactUsHelpText", en: "The PSP customer service team is available Monday through Thursday from 8 AM to 6 PM ET, and on Friday from 8 AM to 5 PM ET. To chat with an online representative, click the \"LIVE HELP\" button in the top-right corner of this page. You can also email us at <a href=\"mailto:PSPhelp@tylertech.com\" title=\"PSPhelp@tylertech.com\">PSPhelp@tylertech.com</a> or call us toll-free at <a href=\"tel:1-877-642-9499\" title=\"1-877-642-9499\">1-877-642-9499</a>.", es: "...", fr: "..." },
               { tag: "home_what_card", en: "PSP Monitoring is an optional new service that automatically informs drivers when their PSP data changes. Drivers who subscribe for the free monitoring service will receive an email each time their PSP record changes. This PSP monitoring service is offered free of charge and is for use by drivers only. To enroll you will need to log into PSP and go to the Driver Dashboard, then click on Enroll in PSP Monitoring.", es: "...", fr: "..." },
               { tag: "PSPHelp", en: "PSP helps carriers make more informed hiring decisions by providing secure, electronic access to a commercial drivers five-year crash and three-year inspection history from the FMCSA Motor Carrier Management Information System (MCMIS). Records are available for commercial drivers, motor carriers, and companies conducting pre-employment screening for the carrier industry.", es: "...", fr: "..." },
               { tag: "Subscribe", en: "Subscribe to Updates", es: "...", fr: "..." },
               { tag: "subscribe_text", en: "Get notified when the latest data snapshot is loaded to PSP.", es: "...", fr: "..." },
               { tag: "home_motus_resources", en: "New FMCSA Motus Rollout Resources Available", es: "...", fr: "..." },
               { tag: "home_motus_resources_txt", en: "Visit the new FAQ for FMCSA Motus rollout support, updates, and contact information.", es: "...", fr: "..." },
               { tag: "home_motus_resources_button", en: "Motus: USDOT Registration System", es: "...", fr: "..." },
               { tag: "areDriver", en: "Are you a driver?", es: "...", fr: "..." }

          ];
          this.filesLoaded = false;
          return this.translations;
     }
}
